import { Loadable } from '@/components/loaders';
import RouteConstants from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { Input } from '@/components/inputs';
import { CustomConnectorDto, ConnectorFlowDTO, ConnectorInstanceSafeDTO } from '@/models/connectors';
import { useCompanyValues } from '@/providers';
import { useGetConnectorFlowsQuery, useGetConnectorInstancesQuery, useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { D365_BC_SYNC_LOGICAL_KEY, filterSourceConnectorWizardFlows, SOURCE_CONNECTOR_WIZARD_DIRECTION } from '../constants';
import { SourceConnectorWizardData } from '../types';

type SourceConnectorSelectionStepProps = {
  wizardData: SourceConnectorWizardData;
  fromCatalogManage?: boolean;
  onNext: (data: Partial<SourceConnectorWizardData>) => void;
  onCancel: () => void;
};

export const SourceConnectorSelectionStep: FC<SourceConnectorSelectionStepProps> = ({
  wizardData,
  fromCatalogManage = false,
  onNext,
  onCancel,
}) => {
  const { t } = useTranslation('data_sources');
  const { id: workspaceId } = useWorkspace();
  const { currentLanguage: lang } = useCompanyValues();

  const isPreSeeded = fromCatalogManage && Boolean(wizardData.connector && wizardData.connectorInstance && wizardData.flow);

  const [name, setName] = useState(wizardData.name);
  const [selectedConnectorId, setSelectedConnectorId] = useState(wizardData.connector?.id ?? '');
  const [selectedFlowId, setSelectedFlowId] = useState(wizardData.flow?.id ?? '');

  const { data: connectors = [], isLoading: isLoadingConnectors } = useGetWorkspaceConnectorsQuery({
    workspaceId,
    lang,
    queryParams: { direction: SOURCE_CONNECTOR_WIZARD_DIRECTION },
  });

  const { data: instances = [], isLoading: isLoadingInstances } = useGetConnectorInstancesQuery({ workspaceId });

  const authenticatedConnectors = useMemo(() => connectors.filter((c) => c.isAuthenticated), [connectors]);

  const selectedConnector = isPreSeeded
    ? (wizardData.connector as CustomConnectorDto)
    : authenticatedConnectors.find((c) => c.id === selectedConnectorId);
  const selectedInstance = useMemo<ConnectorInstanceSafeDTO | undefined>(() => {
    if (isPreSeeded) return wizardData.connectorInstance;
    if (!selectedConnector) return undefined;
    return instances.find((instance) => instance.connectorId === selectedConnector.id);
  }, [instances, isPreSeeded, selectedConnector, wizardData.connectorInstance]);

  const { data: flows = [], isLoading: isLoadingFlows } = useGetConnectorFlowsQuery(
    {
      workspaceId,
      lang,
      queryParams: {
        direction: SOURCE_CONNECTOR_WIZARD_DIRECTION,
        connectorInstanceId: selectedInstance?.id,
      },
    },
    { skip: !selectedInstance?.id || isPreSeeded }
  );

  const wizardFlows = useMemo(() => {
    if (isPreSeeded && wizardData.flow) return [wizardData.flow];
    return filterSourceConnectorWizardFlows(flows);
  }, [flows, isPreSeeded, wizardData.flow]);

  const defaultFlow = wizardFlows.find((flow) => flow.logicalKey === D365_BC_SYNC_LOGICAL_KEY) ?? wizardFlows[0];
  const resolvedFlowId = selectedFlowId || defaultFlow?.id || '';

  const canProceed = Boolean(name.trim() && selectedConnector && selectedInstance && resolvedFlowId);

  const handleNext = () => {
    const connector = selectedConnector as CustomConnectorDto;
    const flow = wizardFlows.find((f) => f.id === resolvedFlowId) as ConnectorFlowDTO;
    onNext({
      name: name.trim(),
      connector,
      connectorInstance: selectedInstance,
      flow,
    });
  };

  if (!isLoadingConnectors && authenticatedConnectors.length === 0) {
    return (
      <div className='flex flex-col gap-4'>
        <p className='text-sm text-secondary-700'>{t('wizard.source_connector.no_authenticated_connectors')}</p>
        <Link
          to={RouteConstants.dataSourcesCatalog}
          className='text-sm font-semibold text-brand-600 hover:underline'
        >
          {t('wizard.source_connector.go_to_catalog')}
        </Link>
        <StepPageActions
          secondaryButton={{ text: 'COMMON.cancel', onClick: onCancel }}
          primaryButton={{ text: 'COMMON.next', onClick: () => {}, disabled: true }}
        />
      </div>
    );
  }

  return (
    <Loadable isLoading={!isPreSeeded && (isLoadingConnectors || isLoadingInstances || isLoadingFlows)}>
      <div className='flex max-w-xl flex-col gap-6'>
        <Input label={t('wizard.source_connector.datasource_name')} value={name} onChange={(e) => setName(e.target.value)} />

        <div className='flex flex-col gap-2'>
          <label className='text-sm font-medium text-secondary-700'>{t('wizard.source_connector.select_connector')}</label>
          {isPreSeeded ? (
            <Input disabled value={selectedConnector?.name ?? ''} />
          ) : (
            <select
              className='rounded-lg border border-secondary-300 px-3 py-2 text-sm'
              value={selectedConnectorId}
              onChange={(e) => {
                setSelectedConnectorId(e.target.value);
                setSelectedFlowId('');
              }}
            >
              <option value=''>{t('wizard.source_connector.select_connector_placeholder')}</option>
              {authenticatedConnectors.map((connector) => (
                <option key={connector.id} value={connector.id}>
                  {connector.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {(wizardFlows.length > 1 || (isPreSeeded && wizardData.flow)) && (
          <div className='flex flex-col gap-2'>
            <label className='text-sm font-medium text-secondary-700'>{t('wizard.source_connector.select_flow')}</label>
            {isPreSeeded ? (
              <Input disabled value={wizardData.flow?.name ?? ''} />
            ) : (
              <select
                className='rounded-lg border border-secondary-300 px-3 py-2 text-sm'
                value={resolvedFlowId}
                onChange={(e) => setSelectedFlowId(e.target.value)}
              >
                {wizardFlows.map((flow) => (
                  <option key={flow.id} value={flow.id}>
                    {flow.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <StepPageActions
          primaryButton={{ text: 'COMMON.next', onClick: handleNext, disabled: !canProceed }}
          secondaryButton={{ text: 'COMMON.cancel', onClick: onCancel }}
        />
      </div>
    </Loadable>
  );
};
